import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";

import { LockedSlots_atom } from "@/features/LockSlots/atoms";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  CreateLessonRequestInput,
  useCreateLessonRequestBookingMutation,
} from "./__generated__/useCreateLessonRequestBookingMutation.graphql";

export const useCreateLessonRequestBooking = () => {
  const { showToast } = useToast();
  const updateLockSlotInfo = useSetAtom(LockedSlots_atom);

  const [createLessonRequestBookingMutation, isCreating] =
    useMutationWithError<useCreateLessonRequestBookingMutation>(
      createLessonRequestBookingMutationString
    );

  const handleCreateLessonRequestBooking = (
    input: CreateLessonRequestInput,
    successCallback?: (bookingId: string) => void
  ) => {
    createLessonRequestBookingMutation({
      variables: { input },
      onCompleted: (response) => {
        const data = response.responsePayload;
        if (data?.success) {
          updateLockSlotInfo(null);
          // Get the first booking ID if available
          const bookingId = data.booking?.id;
          if (bookingId && successCallback) {
            successCallback(bookingId);
          }
        }
      },
      onCompletedErrorCustom: (errors) => {
        showToast({
          description: t`Sorry. An error occurred while submitting your request.`,
          semanticStyle: "error",
          toastId: "create-lesson-request-booking-error",
        });
        return [];
      },
    });
  };

  return [handleCreateLessonRequestBooking, isCreating] as const;
};

const createLessonRequestBookingMutationString = graphql`
  mutation useCreateLessonRequestBookingMutation(
    $input: CreateLessonRequestInput!
  ) {
    responsePayload: createLessonRequest(input: $input) {
      errors {
        field
        code
        message
      }
      success
      booking {
        id
      }
    }
  }
`;
